import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const emptyForm = { imageUrl: '', title: '', description: '', featured: false };

const GalleryEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useScript();
  const { token } = useAuth();
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [titleError, setTitleError] = useState('');

  const isEdit = Boolean(id);
  useDocumentTitle(isEdit ? 'Уређивање ставке галерије — Манастир Градац' : 'Нова ставка галерије — Манастир Градац');

  useEffect(() => {
    if (!id) return;
    api.getPostById(Number(id))
      .then(item => setForm({
        imageUrl: item.imageUrl,
        title: item.title,
        description: item.description,
        featured: item.featured,
      }))
      .catch((err: ApiError) => setError(err?.message || t('Не могу учитати ставку.')));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setTitleError('');
    if (!form.title.trim()) {
      setTitleError(t('Наслов је обавезан.'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEdit && id) {
        await api.updatePost(token, Number(id), form);
      } else {
        await api.createPost(token, form);
      }
      navigate('/galerija');
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr?.message || t('Грешка при чувању. Покушај поново.'));
      setSaving(false);
    }
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="news-edit-page">
      <form className="news-edit-layout" onSubmit={handleSubmit}>
        <div className="news-edit-form-col">
          <h1 className="news-heading" style={{ textAlign: 'left', marginBottom: '28px' }}>
            {isEdit ? t('Уреди ставку галерије') : t('Нова ставка галерије')}
          </h1>

          {error && <div className="form-error-banner" role="alert">{error}</div>}

          <ImageUpload
            label="URL slike"
            imageUrl={form.imageUrl}
            onImageUrlChange={url => setForm(prev => ({ ...prev, imageUrl: url }))}
          />

          <label className="post-admin-label">{t('Наслов')}</label>
          <input
            className="news-edit-input"
            type="text"
            value={form.title}
            onChange={e => {
              if (titleError) setTitleError('');
              set('title')(e);
            }}
            aria-invalid={Boolean(titleError)}
          />
          {titleError && <div className="field-error" role="alert">{titleError}</div>}

          <label className="post-admin-label">{t('Опис')}</label>
          <textarea
            className="news-edit-textarea"
            rows={10}
            value={form.description}
            onChange={set('description')}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <input type="checkbox" checked={form.featured}
              onChange={e => setForm(prev => ({ ...prev, featured: e.target.checked }))} />
            {t('Прикажи на почетној страни')}
          </label>

          <div style={{ height: '80px' }} />
        </div>

        <div className="news-edit-preview-col">
          <p className="post-admin-label" style={{ marginBottom: '10px' }}>{t('Предпреглед')}</p>
          <div className="news-edit-preview-card">
            {form.imageUrl && (
              <div className="news-image" style={{ aspectRatio: '16/9', height: 'auto' }}>
                <img
                  src={form.imageUrl}
                  alt={form.title}
                  width={800}
                  height={450}
                  decoding="async"
                />
              </div>
            )}
            {!form.imageUrl && (
              <div className="news-image news-image-placeholder" />
            )}
            <div className="news-body">
              <h3 className="news-title">{form.title || t('Наслов...')}</h3>
              <p className="news-excerpt">{form.description || t('Опис...')}</p>
            </div>
          </div>
        </div>

        <div className="news-edit-sticky-bar">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t('Чување...') : t('Сачувај')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            {t('Откажи')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryEditPage;
